import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { LogIn, Mail, Lock, KeyRound, Factory, Hash, Shield } from "lucide-react";
import { toast } from "sonner";
import { homeForRole } from "@/lib/roles";

export default function Login() {
    const { login, error, setError } = useAuth();
    const nav = useNavigate();
    const [mode, setMode] = useState("codice"); // "codice" | "email"
    const [codice, setCodice] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    // Preselezione codice da localStorage
    useEffect(() => {
        const c = localStorage.getItem("produx_last_codice");
        if (c) setCodice(c);
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setError("");
        const payload = mode === "codice"
            ? { codice: codice.padStart(4, "0"), password }
            : { email, password };
        const usr = await login(payload);
        setBusy(false);
        if (usr) {
            if (mode === "codice") localStorage.setItem("produx_last_codice", codice.padStart(4, "0"));
            toast.success("Accesso effettuato");
            nav(usr.must_change_password ? "/change-password" : homeForRole(usr.role));
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4 relative z-10">
            <div className="w-full max-w-md">
                <div className="mb-8 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[10px] font-mono tracking-[0.3em] mb-4">
                        <Factory size={12} /> GESTIONALE PRODUZIONE
                    </div>
                    <h1 className="font-display text-3xl md:text-4xl font-extrabold text-slate-50">
                        Produzione &amp; Magazzino
                    </h1>
                    <p className="mt-2 text-sm text-slate-400">Controllo operativo, distinte, uscite merce.</p>
                </div>

                <Card className="card p-6 md:p-8 bg-transparent">
                    <div className="flex gap-2 mb-6 p-1 bg-slate-950/60 rounded-lg border border-slate-800">
                        <button
                            data-testid="tab-mode-codice"
                            type="button"
                            onClick={() => { setMode("codice"); setError(""); }}
                            className={`flex-1 py-2 text-xs font-semibold tracking-wider rounded-md transition inline-flex items-center justify-center gap-2 ${mode==='codice' ? 'bg-amber-500 text-slate-950' : 'text-slate-400 hover:text-slate-200'}`}
                        ><Hash size={12} /> CODICE</button>
                        <button
                            data-testid="tab-mode-email"
                            type="button"
                            onClick={() => { setMode("email"); setError(""); }}
                            className={`flex-1 py-2 text-xs font-semibold tracking-wider rounded-md transition inline-flex items-center justify-center gap-2 ${mode==='email' ? 'bg-amber-500 text-slate-950' : 'text-slate-400 hover:text-slate-200'}`}
                        ><Shield size={12} /> ADMIN</button>
                    </div>

                    <form onSubmit={submit} className="space-y-4">
                        {mode === "codice" ? (
                            <div>
                                <Label className="text-xs text-slate-400 mb-1.5 block">Codice utente (4 cifre)</Label>
                                <div className="relative">
                                    <Hash size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                    <Input data-testid="input-codice" type="text" inputMode="numeric" pattern="\d*"
                                           maxLength={4} value={codice}
                                           onChange={(e)=>setCodice(e.target.value.replace(/\D/g,""))}
                                           required autoFocus
                                           className="pl-9 bg-slate-950/50 border-slate-800 h-11 font-mono text-lg tracking-widest" />
                                </div>
                                <div className="text-[10px] text-slate-500 mt-1">Ricevuto dall'amministratore. Esempio: 0007</div>
                            </div>
                        ) : (
                            <div>
                                <Label className="text-xs text-slate-400 mb-1.5 block">E-mail admin</Label>
                                <div className="relative">
                                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                    <Input data-testid="input-email" type="email" value={email}
                                           onChange={(e)=>setEmail(e.target.value)} required
                                           className="pl-9 bg-slate-950/50 border-slate-800 h-11" />
                                </div>
                            </div>
                        )}
                        <div>
                            <Label className="text-xs text-slate-400 mb-1.5 block">Password</Label>
                            <div className="relative">
                                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                <Input data-testid="input-password" type="password" value={password}
                                       onChange={(e)=>setPassword(e.target.value)} required
                                       className="pl-9 bg-slate-950/50 border-slate-800 h-11" />
                            </div>
                            {mode === "codice" && (
                                <div className="text-[10px] text-slate-500 mt-1">
                                    Password dimenticata? Chiedi all'amministratore di reimpostarla.
                                </div>
                            )}
                        </div>

                        {error && (
                            <div data-testid="auth-error" className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
                                {error}
                            </div>
                        )}

                        <Button data-testid="button-submit-auth" type="submit" disabled={busy}
                                className="w-full h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold gap-2">
                            {busy ? <KeyRound size={16} className="animate-pulse" /> : <LogIn size={16} />}
                            {busy ? "Attendere…" : "Entra"}
                        </Button>
                    </form>
                </Card>

                <p className="mt-6 text-center text-[11px] text-slate-500 font-mono tracking-wider">
                    PRODUX · v1.0 · gestionale di produzione
                </p>
            </div>
        </div>
    );
}
